/**
 * How a `SpaceDocumentProvider` reaches the server. The interface keeps the
 * provider testable with a fake; `makeEnvironmentSpaceDocumentTransport` is
 * the real one, over the environment's WebSocket RPC session.
 *
 * The real subscription follows the connection supervisor: when the socket
 * drops it reports `onConnection(false)`, and when a new session comes up it
 * subscribes again, which delivers a fresh `sync`. A domain failure (the
 * document not found, storage unavailable) ends it with `onError`.
 *
 * @module spaces/realtime/transport
 */
import { EnvironmentSupervisor } from "@amoreg/client-runtime/connection";
import { subscribe } from "@amoreg/client-runtime/rpc";
import {
  createEnvironmentRpcCommand,
  runAtomCommand,
  runInEnvironment,
  squashAtomCommandFailure,
} from "@amoreg/client-runtime/state/runtime";
import {
  type EnvironmentId,
  type SpaceDocumentPushAwarenessInput,
  type SpaceDocumentPushUpdateInput,
  type SpaceDocumentPushUpdateResult,
  type SpaceDocumentRef,
  type SpaceDocumentStreamEvent,
  WS_METHODS,
} from "@amoreg/contracts";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";

import { connectionAtomRuntime } from "../../connection/runtime";
import { appAtomRegistry } from "../../rpc/atomRegistry";

export interface SpaceDocumentSubscriptionHandlers {
  readonly onEvent: (event: SpaceDocumentStreamEvent) => void;
  /** The subscription failed for good; it delivers nothing more. */
  readonly onError: (error: unknown) => void;
  /** The connection went down (false) or came back (true). */
  readonly onConnection: (online: boolean) => void;
}

export interface SpaceDocumentTransport {
  /** Start the subscription; the returned function ends it. */
  readonly subscribe: (
    ref: SpaceDocumentRef,
    handlers: SpaceDocumentSubscriptionHandlers,
  ) => () => void;
  readonly pushUpdate: (
    input: SpaceDocumentPushUpdateInput,
  ) => Promise<SpaceDocumentPushUpdateResult>;
  readonly pushAwareness: (input: SpaceDocumentPushAwarenessInput) => Promise<void>;
}

const pushUpdateCommand = createEnvironmentRpcCommand(connectionAtomRuntime, {
  label: "web:spaces:push-document-update",
  tag: WS_METHODS.spacesPushDocumentUpdate,
});

const pushAwarenessCommand = createEnvironmentRpcCommand(connectionAtomRuntime, {
  label: "web:spaces:push-awareness",
  tag: WS_METHODS.spacesPushAwareness,
});

const QUIET = { reportFailure: false, reportDefect: false } as const;

/** The real transport, over one environment's RPC session. */
export const makeEnvironmentSpaceDocumentTransport = (
  environmentId: EnvironmentId,
): SpaceDocumentTransport => ({
  subscribe: (ref, handlers) => {
    const program = Effect.gen(function* () {
      const supervisor = yield* EnvironmentSupervisor;
      const connection = SubscriptionRef.changes(supervisor.session).pipe(
        Stream.map(Option.isSome),
        Stream.changes,
        Stream.runForEach((online) => Effect.sync(() => handlers.onConnection(online))),
      );
      const events = subscribe(WS_METHODS.spacesSubscribeDocument, ref).pipe(
        Stream.runForEach((event) => Effect.sync(() => handlers.onEvent(event))),
      );
      yield* Effect.all([events, connection], { concurrency: "unbounded", discard: true });
    });
    const atom = connectionAtomRuntime.atom(
      runInEnvironment(environmentId, program).pipe(
        Effect.catchCause((cause) =>
          Cause.hasInterruptsOnly(cause)
            ? Effect.void
            : Effect.sync(() => handlers.onError(Cause.squash(cause))),
        ),
      ),
    );
    return appAtomRegistry.mount(atom);
  },
  pushUpdate: async (input) => {
    const result = await runAtomCommand(
      appAtomRegistry,
      pushUpdateCommand,
      { environmentId, input },
      QUIET,
    );
    if (result._tag === "Success") return result.value;
    throw squashAtomCommandFailure(result);
  },
  pushAwareness: async (input) => {
    const result = await runAtomCommand(
      appAtomRegistry,
      pushAwarenessCommand,
      { environmentId, input },
      QUIET,
    );
    if (result._tag === "Failure") throw squashAtomCommandFailure(result);
  },
});
